import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { ModalSummaryHeader } from '../ApplicationComponents';
import { TabPanel, Item } from 'devextreme-react/tab-panel';
import { FileList } from '../components/FileList';
import { handleAdminError, handleAdminSuccess, handleAdminInfo, handleAdminWarning } from '../../../common/SharedFunctions';
import { ApplyFileStatusType } from '@apply/constants';

export const ApplicationFilesModal = () => {
    const { isOpen, id, propertyId } = useSelector((state) => state.application?.applicationFilesModal);
    const { dispatch } = store;

    const [application, set_application] = useState(null);
    const [applicationFiles, set_applicationFiles] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        formRow: { marginBottom: '0' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        propertyTitle: {
            backgroundColor: 'whitesmoke',
            paddingLeft: '10px',
            paddingTop: '5px',
            paddingBottom: '5px',
        },
        propertyAddress: {
            marginTop: '5px',
            marginBottom: '10px',
            fontWeight: 'bold',
        },
        propertyFileContainer: {
            padding: '15px',
            width: '100%',
        },
        files: {
            padding: '20px',
        },
    };

    const fileStatuses = [
        { id: ApplyFileStatusType.No, name: 'Not Provided' },
        { id: ApplyFileStatusType.Pending, name: 'Pending' },
        { id: ApplyFileStatusType.Approved, name: 'Approved' },
        { id: ApplyFileStatusType.NotApplicable, name: 'Upload/Replace' },
        { id: ApplyFileStatusType.Rejected, name: 'Rejected' },
    ];

    const copyFileStatuses = (fileExists) => {
        const output = [];
        fileStatuses.forEach((s) => {
            if (s.id !== 0 || !fileExists) {
                var status = { ...s };
                if (status.id === -1) {
                    status.name = fileExists ? 'Replace' : 'Upload';
                }
                output.push(status);
            }
        });
        return output;
    };

    const loadApplicationAndFiles = async (id) => {
        try {
            set_loadingMessage('Loading Files...');

            const response = (await applicationApi.getApplicationFileSummary(id)).result;
            if (!response) {
                return;
            }

            set_application(response);

            response.files.forEach((file) => {
                if (file.fileTypeId !== 1) {
                    //No status options for Termsheet
                    file.statuses = copyFileStatuses(!!file.url);
                } else {
                    file.createdDate = response.createdDate;
                }
            });

            response.properties.forEach((p) => {
                if (propertyId && p.id !== propertyId) {
                    p.disabled = true;
                }
                p.files.forEach((file) => {
                    file.statuses = copyFileStatuses(!!file.url);
                });
            });

            set_applicationFiles(response.files);
        } catch (ex) {
            handleAdminError(ex, 'Unexpected Error loading application! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setApplicationFilesModal({
                applicationFilesModal: { isOpen: false },
            })
        );

    useEffect(() => {
        async function isOpenChanged() {
            resetState();
            if (!isOpen || !id) {
                return;
            }
            await loadApplicationAndFiles(id);
        }

        isOpenChanged();
    }, [isOpen, id]);

    const resetState = () => {
        set_application(null);
        set_applicationFiles([]);
    };

    const renderPropertyItem = (property) => {
        return (
            <div>
                <div style={styles.propertyTitle}>
                    {property.description && (
                        <div>
                            <b>
                                Property {property.bfiNumberSuffix} - {property.description}
                            </b>
                        </div>
                    )}
                    {property.address && property?.address?.street1.length > 0 && (
                        <div style={styles.propertyAddress}>
                            {property.address?.street1} {property.address?.street2} {property.address?.city},{' '}
                            {property.address?.state} {property.address?.zipCode}
                        </div>
                    )}
                </div>

                {property.files && (
                    <div style={styles.files}>
                        <FileList
                            parentId={property.id}
                            files={property.files}
                            isApplicationLevel={false}
                            disabled={true}
                            readonly={true}
                        />
                    </div>
                )}
                <hr />
            </div>
        );
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '700px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>
                                        {application?.properties?.length > 1 && propertyId ? 'Property' : 'Application'} Files
                                    </h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <ModalSummaryHeader application={application} />
                                <div
                                    className='scrollable-content'
                                    style={{ margin: '10px' }}
                                >
                                    <TabPanel
                                        tabsPosition={'left'}
                                        height={'100%'}
                                    >
                                        {(application?.properties?.length == 1 || !propertyId) && (
                                            <Item title='Application Files'>
                                                <div style={styles.files}>
                                                    <FileList
                                                        files={applicationFiles}
                                                        isApplicationLevel={true}
                                                        disabled={true}
                                                        readonly={true}
                                                    />
                                                </div>
                                            </Item>
                                        )}
                                        <Item title={'Property Files'}>
                                            <div style={styles.propertyFileContainer}>
                                                {propertyId &&
                                                    application?.properties
                                                        ?.filter((p) => p.id == propertyId)
                                                        .map((p) => {
                                                            return renderPropertyItem(p);
                                                        })}
                                                {!propertyId &&
                                                    application?.properties?.map((p) => {
                                                        return renderPropertyItem(p);
                                                    })}
                                            </div>
                                        </Item>
                                    </TabPanel>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        type='button'
                                        className='button primary'
                                        data-dismiss='modal'
                                        onClick={_handleClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};
